import { useVirtualizer } from '@tanstack/react-virtual'
import { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { EyeIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline'

interface QueryResult {
  columns: string[]
  rows: unknown[][]
  rowCount: number
  executionTime: number
  executionTimeMs?: number
  status?: 'success' | 'error' | 'pending'
  message?: string
  query: string
  timestamp: Date
  affectedRows?: number
}

interface QueryResultsProps {
  result: QueryResult
  isLoading: boolean
  databaseContext?: {
    connectionType?: string
    database?: string
    schema?: string
  }
  onExport?: () => void
}

interface DataViewerModalProps {
  isOpen: boolean
  onClose: () => void
  columnName: string
  value: unknown
  rowIndex: number
}

interface ClickableCellProps {
  displayContent: string
  onClick: () => void
  isMonospace?: boolean
  previewContent: string
}

const DataViewerModal: React.FC<DataViewerModalProps> = ({ 
  isOpen, 
  onClose, 
  columnName, 
  value, 
  rowIndex 
}) => {
  const [copied, setCopied] = useState(false)
  
  if (!isOpen) return null

  const formatValue = () => {
    if (value === null || value === undefined) return 'NULL'
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2)
      } catch {
        return '[object Object]'
      }
    }
    return typeof value === 'string' ? value : JSON.stringify(value)
  }

  const isBinary = () => {
    if (typeof value !== 'string') return false
    // Check for common binary patterns
    // Check for control characters that indicate binary data
    const hasControlChars = value.split('').some(char => {
      const code = char.charCodeAt(0)
      return (code >= 0 && code <= 8) || code === 11 || code === 12 ||
             (code >= 14 && code <= 31) || (code >= 127 && code <= 159)
    })
    return value.includes(String.raw`\x`) || hasControlChars
  }
  
  const handleCopy = () => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(formatValue())
        setCopied(true)
        setTimeout(() => { setCopied(false) }, 2000)
      } catch (err) {
        console.error('Failed to copy:', err)
      }
    })()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-3xl max-h-[80vh] w-full mx-4 overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Data Viewer
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Column: <span className="font-medium">{columnName}</span> | Row: <span className="font-medium">{rowIndex + 1}</span>
            </p>
          </div>
          <button type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex-1 overflow-auto">
          {isBinary() ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <p className="text-lg mb-2">Binary Data</p>
              <p className="text-sm">Cannot display binary content</p>
            </div>
          ) : (
            <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded text-sm font-mono whitespace-pre-wrap break-all text-gray-800 dark:text-gray-200">
              {formatValue()}
            </pre>
          )}
        </div>
        
        <div className="mt-4 flex justify-between">
          <button type="button"
            onClick={handleCopy}
            disabled={isBinary()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {copied ? (
              <>
                <CheckIcon className="w-4 h-4" />
                Copied!
              </>
            ) : (
              <>
                <ClipboardDocumentIcon className="w-4 h-4" />
                Copy to Clipboard
              </>
            )}
          </button>
          <button type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

const ClickableCell: React.FC<ClickableCellProps> = ({ 
  displayContent, 
  onClick, 
  isMonospace = false,
  previewContent 
}) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [mounted, setMounted] = useState(false)
  
  useEffect(() => {
    setMounted(true)
  }, [])
  
  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft
    const scrollY = window.pageYOffset || document.documentElement.scrollTop
    
    setTooltipPosition({ 
      x: rect.left + scrollX, 
      y: rect.bottom + scrollY + 5 
    })
    setShowTooltip(true)
  }
  
  const handleMouseLeave = () => {
    setShowTooltip(false)
  }
  
  const tooltip = showTooltip && mounted ? createPortal(
    <div 
      className="fixed z-[9999] px-3 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg shadow-xl max-w-md border border-gray-700 dark:border-gray-300"
      style={{ 
        left: `${tooltipPosition.x}px`, 
        top: `${tooltipPosition.y}px`,
        pointerEvents: 'none'
      }}
    >
      <div className={`${isMonospace ? 'font-mono' : ''} whitespace-pre-wrap break-all`}>
        {previewContent}
      </div>
    </div>,
    document.body
  ) : null
  
  return (
    <>
      <div 
        className="relative cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 group"
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span className={`${isMonospace ? 'font-mono' : ''} text-xs block truncate group-hover:pr-4`}>
          {displayContent}
        </span>
        <EyeIcon className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity absolute right-0 top-1/2 -translate-y-1/2" />
      </div>
      {tooltip}
    </>
  )
}

export function QueryResults({ result, isLoading, databaseContext, onExport }: QueryResultsProps) {
  const parentRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [dataViewerModal, setDataViewerModal] = useState<{
    isOpen: boolean
    columnName: string
    value: unknown
    rowIndex: number
  }>({ isOpen: false, columnName: '', value: null, rowIndex: 0 })

  // Format execution time with proper units and precision
  const formatExecutionTime = (executionTimeMs?: number, fallbackExecutionTime?: number) => {
    const timeMs = executionTimeMs ?? (fallbackExecutionTime ? fallbackExecutionTime * 1000 : 0);
    
    if (timeMs === 0) return '< 1ms';
    
    if (timeMs < 1000) {
      return `${Math.round(timeMs)}ms`;
    } else if (timeMs < 60000) {
      return `${(timeMs / 1000).toFixed(2)}s`;
    } else {
      const mins = Math.floor(timeMs / 60000);
      const secs = ((timeMs % 60000) / 1000).toFixed(2);
      return `${mins}m ${secs}s`;
    }
  }

  // Get performance indicator color based on execution time
  const getPerformanceColor = (executionTimeMs?: number) => {
    const timeMs = executionTimeMs || 0;
    
    if (timeMs < 100) {
      return 'text-green-600 dark:text-green-400';
    } else if (timeMs < 1000) {
      return 'text-yellow-600 dark:text-yellow-400';
    } else if (timeMs < 5000) {
      return 'text-orange-600 dark:text-orange-400';
    } else {
      return 'text-red-600 dark:text-red-400';
    }
  }

  // Ensure result is properly structured with safety defaults
  const safeResult = {
    ...result,
    columns: Array.isArray(result.columns) 
      ? result.columns
      : [],
    rows: Array.isArray(result.rows) 
      ? result.rows.map(row => Array.isArray(row) ? row : Object.values(row))
      : [],
  }

  // Calculate dynamic column widths based on column name and data content
  const getColumnWidth = useCallback((columnName: string, columnIndex: number) => {
    const minWidth = 80; // Minimum column width for readability
    const maxWidth = 400; // Maximum column width to prevent overly wide columns
    const charWidth = 7; // approximate character width in pixels for text-xs
    const padding = 48; // padding for column header and cell content
    
    // Start with column name width
    const nameBasedWidth = columnName.length * charWidth + padding;
    
    // Intelligently sample rows to determine content width
    const rowsToSample = Math.min(30, safeResult.rows.length); // Sample up to 30 rows
    let contentLengths: number[] = [];
    let dataType: 'number' | 'date' | 'uuid' | 'json' | 'boolean' | 'text' | 'mixed' = 'mixed';
    let hasLongContent = false;
    
    // Analyze data patterns in the column
    for (let i = 0; i < rowsToSample; i++) {
      const cellValue = safeResult.rows[i]?.[columnIndex];
      if (cellValue === null || cellValue === undefined) continue;
      
      let valueStr: string;
      if (typeof cellValue === 'object') {
        valueStr = JSON.stringify(cellValue);
        if (dataType === 'mixed') dataType = 'json';
      } else if (typeof cellValue === 'boolean') {
        valueStr = cellValue ? 'TRUE' : 'FALSE';
        if (dataType === 'mixed') dataType = 'boolean';
      } else {
        valueStr = typeof cellValue === 'string' ? cellValue : 
                   typeof cellValue === 'number' || typeof cellValue === 'boolean' ? String(cellValue) :
                   cellValue !== null && cellValue !== undefined && typeof cellValue === 'object' ? JSON.stringify(cellValue) : '';
        
        // Detect data type patterns
        if (dataType === 'mixed') {
          if (typeof cellValue === 'number' || (!Number.isNaN(Number(cellValue)) && /^-?\d+(\.\d+)?$/.test(valueStr))) {
            dataType = 'number';
          } else if (/^\d{4}-\d{2}-\d{2}/.test(valueStr)) {
            dataType = 'date';
          } else if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(valueStr)) {
            dataType = 'uuid';
          } else {
            dataType = 'text';
          }
        }
      }
      
      contentLengths.push(valueStr.length);
      if (valueStr.length > 100) hasLongContent = true;
    }
    
    // Calculate optimal width based on data type and content
    let optimalWidth = minWidth;
    
    if (contentLengths.length > 0) {
      // Sort lengths to find percentiles (avoid outliers)
      contentLengths.sort((a, b) => a - b);
      
      // Use 90th percentile to avoid extreme outliers
      const percentileIndex = Math.floor(contentLengths.length * 0.9);
      const typicalLength = contentLengths[percentileIndex];
      
      // Apply different strategies based on data type
      switch (dataType) {
        case 'boolean':
          optimalWidth = 90; // Booleans are short
          break;
          
        case 'number':
          // Numbers typically need less width
          optimalWidth = Math.min(typicalLength * charWidth + padding, 150);
          break;
          
        case 'date':
          // Dates have predictable length
          optimalWidth = Math.min(typicalLength * charWidth + padding, 180);
          break;
          
        case 'uuid':
          // UUIDs have fixed length (36 chars)
          optimalWidth = 280; // Fixed width for UUIDs
          break;
          
        case 'json':
          // JSON needs more space but not too much (users can click to expand)
          if (hasLongContent) {
            optimalWidth = 250;
          } else {
            optimalWidth = Math.min(typicalLength * charWidth * 0.7 + padding, 300);
          }
          break;
          
        case 'text':
        case 'mixed':
          // For text and mixed content, be more generous with width but still reasonable
          if (typicalLength <= 20) {
            optimalWidth = typicalLength * charWidth + padding;
          } else if (typicalLength <= 50) {
            // Medium length text - provide good visibility
            optimalWidth = Math.min(typicalLength * charWidth * 0.8 + padding, 250);
          } else if (typicalLength <= 100) {
            // Longer text - start capping more aggressively
            optimalWidth = Math.min(200 + (typicalLength - 50) * 2, 300);
          } else {
            // Very long text - cap at reasonable width (users can click to see full)
            optimalWidth = 300;
          }
          break;
        default:
          // Should be unreachable because all union variants are covered
          break;
      }
    }
    
    // Consider column name width as minimum
    const finalWidth = Math.max(nameBasedWidth, optimalWidth);
    
    // Apply final bounds
    return Math.max(minWidth, Math.min(finalWidth, maxWidth));
  }, [safeResult.rows])

  // Measure available horizontal space and expand columns smartly to utilize it
  const [containerWidth, setContainerWidth] = useState<number>(0)

  useEffect(() => {
    const target = parentRef.current || containerRef.current
    if (!target) return
    // Initialize after layout
    const measure = () => setContainerWidth(target.clientWidth)
    const raf = requestAnimationFrame(measure)
    // Observe size changes of the scroll container
    const observer = new ResizeObserver(entries => {
      const [entry] = entries
      if (entry) {
        setContainerWidth(Math.floor(entry.contentRect.width))
      }
    })
    observer.observe(target)
    // Fallback: update on window resize
    const onResize = () => measure()
    window.addEventListener('resize', onResize)
    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
      window.removeEventListener('resize', onResize)
    }
  }, [])

  // Re-measure when result shape changes (columns/rows can influence layout)
  useEffect(() => {
    const target = parentRef.current || containerRef.current
    if (!target) return
    setContainerWidth(target.clientWidth)
  }, [safeResult.columns.length, safeResult.rows.length])

  const rowNumberWidth = 40 // w-10 = 2.5rem = 40px

  // Precompute base widths once per result set
  const baseColumnWidths = useMemo(() => {
    return safeResult.columns.map((column, index) => {
      const columnName = typeof column === 'string' ? column : JSON.stringify(column)
      return getColumnWidth(columnName, index)
    })
  }, [safeResult.columns, getColumnWidth])

  const baseTableWidth = useMemo(() => {
    return rowNumberWidth + baseColumnWidths.reduce((sum, w) => sum + w, 0)
  }, [baseColumnWidths])

  // Compute a more generous desired width based on content, used only when there is free space
  const computeDesiredWidth = useCallback((columnIndex: number, columnName: string): number => {
    const minWidth = 80
    const maxDesired = 600 // allow wider columns when space permits
    const charWidth = 7
    const padding = 48

    const rowsToSample = Math.min(50, safeResult.rows.length)
    const lengths: number[] = []
    let dataType: 'number' | 'date' | 'uuid' | 'json' | 'boolean' | 'text' | 'mixed' = 'mixed'
    let hasVeryLong = false

    for (let i = 0; i < rowsToSample; i++) {
      const cellValue = safeResult.rows[i]?.[columnIndex]
      if (cellValue === null || cellValue === undefined) continue
      let valueStr: string
      if (typeof cellValue === 'object') {
        valueStr = JSON.stringify(cellValue)
        if (dataType === 'mixed') dataType = 'json'
      } else if (typeof cellValue === 'boolean') {
        valueStr = cellValue ? 'TRUE' : 'FALSE'
        if (dataType === 'mixed') dataType = 'boolean'
      } else {
        valueStr = typeof cellValue === 'string' ? cellValue : 
                   typeof cellValue === 'number' || typeof cellValue === 'boolean' ? String(cellValue) :
                   cellValue !== null && cellValue !== undefined && typeof cellValue === 'object' ? JSON.stringify(cellValue) : ''
        if (dataType === 'mixed') {
          if (typeof cellValue === 'number' || (!Number.isNaN(Number(cellValue)) && /^-?\d+(\.\d+)?$/.test(valueStr))) {
            dataType = 'number'
          } else if (/^\d{4}-\d{2}-\d{2}/.test(valueStr)) {
            dataType = 'date'
          } else if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(valueStr)) {
            dataType = 'uuid'
          } else {
            dataType = 'text'
          }
        }
      }
      lengths.push(valueStr.length)
      if (valueStr.length > 150) hasVeryLong = true
    }

    const nameWidth = columnName.length * charWidth + padding

    if (lengths.length === 0) {
      return Math.max(minWidth, Math.min(nameWidth, maxDesired))
    }

    lengths.sort((a, b) => a - b)
    const percentileIndex = Math.floor(lengths.length * 0.95) // be slightly more generous
    const p95 = lengths[Math.max(0, Math.min(percentileIndex, lengths.length - 1))]

    let desired = minWidth
    switch (dataType) {
      case 'boolean':
        desired = 90
        break
      case 'number':
        desired = Math.min(p95 * charWidth + padding, 200)
        break
      case 'date':
        desired = Math.min(p95 * charWidth + padding, 220)
        break
      case 'uuid':
        desired = 320
        break
      case 'json':
        desired = hasVeryLong ? 320 : Math.min(p95 * charWidth + padding, 340)
        break
      case 'text':
      case 'mixed':
        if (p95 <= 20) {
          desired = p95 * charWidth + padding
        } else if (p95 <= 80) {
          desired = Math.min(260 + (p95 - 40) * 1.5, 420)
        } else {
          desired = 480
        }
        break
      default:
        break
    }

    const final = Math.max(nameWidth, desired)
    return Math.max(minWidth, Math.min(final, maxDesired))
  }, [safeResult.rows])

  // Expand columns to consume spare space, prioritizing columns that still have truncated content
  const expandedColumnWidths = useMemo(() => {
    const base = baseColumnWidths
    if (base.length === 0) return base
    const container = containerWidth || 0
    const baseTotal = baseTableWidth
    const available = Math.max(0, container - baseTotal)
    if (available <= 0) return base

    // Determine desired widths for each column
    const desired = safeResult.columns.map((column, index) => {
      const columnName = typeof column === 'string' ? column : JSON.stringify(column)
      return computeDesiredWidth(index, columnName)
    })

    // Compute needed extra per column
    const need = desired.map((d, i) => Math.max(0, d - base[i]))
    const totalNeed = need.reduce((s, n) => s + n, 0)
    if (totalNeed === 0) return base

    // Distribute available space proportionally
    const increments = need.map(n => (available >= totalNeed ? n : (n / totalNeed) * available))
    let expanded = base.map((w, i) => Math.round(w + increments[i]))

    // If we still have leftover width after meeting "desired" needs, distribute it by desired weights
    const leftover = Math.max(0, available - totalNeed)
    if (leftover > 0) {
      const weightSum = desired.reduce((s, d) => s + Math.max(1, d), 0)
      if (weightSum > 0) {
        const extra = desired.map(d => (Math.max(1, d) / weightSum) * leftover)
        expanded = expanded.map((w, i) => Math.round(w + extra[i]))
      }
    }
    return expanded
  }, [baseColumnWidths, baseTableWidth, containerWidth, safeResult.columns, computeDesiredWidth])

  // Use expanded widths for layout
  const totalTableWidth = useMemo(() => {
    const sum = expandedColumnWidths.reduce((s, w) => s + w, 0)
    // Ensure table stretches at least to container width to avoid leftover gutter
    return Math.max(rowNumberWidth + sum, containerWidth || 0)
  }, [expandedColumnWidths, containerWidth])

  // Check if column name should be truncated and show tooltip
  const shouldTruncateColumn = (columnName: string) => {
    return columnName.length > 20; // Truncate if longer than 20 characters
  }

  // Get truncated column name
  const getTruncatedColumnName = (columnName: string) => {
    return shouldTruncateColumn(columnName) ? `${columnName.substring(0, 17)}...` : columnName;
  }

  // Helper functions for database name truncation
  const shouldTruncateDatabaseName = (fullName: string) => {
    return fullName.length > 30; // Truncate if longer than 30 characters
  }

  const getTruncatedDatabaseName = (fullName: string) => {
    return shouldTruncateDatabaseName(fullName) ? `${fullName.substring(0, 27)}...` : fullName;
  }

  // Fallback width when there are no columns
  const emptyTableWidth = rowNumberWidth + 200

  const rowVirtualizer = useVirtualizer({
    count: safeResult.rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 32, // Reduced from 40 to 32 for more compact rows
    overscan: 10,
  })

  const formatCellValue = (value: unknown, columnName: string, rowIndex: number, columnWidth: number) => {
    // Calculate available space without reserving for eye icon (only reserve when actually needed)
    // Accounting for padding (px-2 = 16px total) and small buffer
    const availableWidthFull = columnWidth - 20; // 16px padding + 4px buffer
    const availableWidthWithIcon = columnWidth - 36; // 16px padding + 16px eye icon + 4px buffer
    const charWidth = 7; // approximate character width in pixels for text-xs
    const maxVisibleCharsFull = Math.floor(availableWidthFull / charWidth);
    const maxVisibleCharsWithIcon = Math.floor(availableWidthWithIcon / charWidth);
    
    const isContentTruncated = (content: string) => {
      // Content is truncated if it doesn't fit in the full available space
      return content.length > maxVisibleCharsFull;
    }
    
    const handleCellClick = () => {
      setDataViewerModal({
        isOpen: true,
        columnName,
        value,
        rowIndex
      })
    }
    
    const getPreviewContent = (content: string) => {
      if (content.length <= 150) return content;
      return `${content.substring(0, 150)}...`;
    }

    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic text-xs">NULL</span>
    }

    if (typeof value === 'boolean') {
      return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
          value 
            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
        }`}>
          {value ? 'TRUE' : 'FALSE'}
        </span>
      )
    }

    if (typeof value === 'object') {
      const jsonStr = JSON.stringify(value)
      const isTruncated = isContentTruncated(jsonStr)
      // Use column-width-based truncation; reserve icon space only when truncated
      const visibleChars = isTruncated ? maxVisibleCharsWithIcon : maxVisibleCharsFull
      const display = jsonStr.length > visibleChars ? `${jsonStr.substring(0, visibleChars)}...` : jsonStr
      
      return isTruncated ? (
          <ClickableCell
            displayContent={display}
            onClick={handleCellClick}
            isMonospace={true}
            previewContent={getPreviewContent(JSON.stringify(value, null, 2))}
          />
        ) : (
          <span className="font-mono text-xs block truncate" title={JSON.stringify(value, null, 2)}>
            {display}
          </span>
        );
    }

    const stringValue = typeof value === 'string' ? value : JSON.stringify(value)
    
    // Check if it looks like a number (and don't make it clickable)
    if (typeof value === 'number' || (!Number.isNaN(Number(value)) && typeof value !== 'string')) {
      return <span className="font-mono text-xs tabular-nums">{stringValue}</span>
    }
    
    // For all other values (strings, dates, etc), check if they need truncation
    const isTruncated = isContentTruncated(stringValue)
    // If truncated, use space with icon; otherwise use full space
    const cellContent = isTruncated ? `${stringValue.substring(0, maxVisibleCharsWithIcon)}...` : stringValue
    
    // Check if it looks like a date and format accordingly
    const isDate = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value);
    
    return isTruncated ? (
        <ClickableCell
          displayContent={cellContent}
          onClick={handleCellClick}
          isMonospace={isDate}
          previewContent={getPreviewContent(stringValue)}
        />
      ) : (
        <span className={`text-xs block truncate ${isDate ? 'font-mono' : ''}`} title={stringValue}>
          {stringValue}
        </span>
      );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-primary-600 rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Executing query...</p>
        </div>
      </div>
    )
  }

  // Handle pending results
  if (result.status === 'pending') {
    return (
      <div className="h-full flex flex-col">
        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              {result.message || 'Query not executed yet'}
            </h3>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <div className="text-yellow-600 dark:text-yellow-400 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Ready to Execute
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Press Ctrl+Enter or click the Run button to execute this query
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Handle error results
  if (result.status === 'error') {
    return (
      <div className="h-full flex flex-col">
        {/* Consolidated Error Query Info Header */}
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 text-sm">
              {/* Title and Database Context */}
              <h3 className="font-semibold text-gray-900 dark:text-white">Query Results</h3>
              {databaseContext?.database && (
                <div className="text-gray-600 dark:text-gray-400 relative group">
                  {(() => {
                    const fullName = databaseContext.connectionType === 'postgresql' && databaseContext.schema
                      ? `${databaseContext.database}.${databaseContext.schema}`
                      : databaseContext.database;
                    const displayName = getTruncatedDatabaseName(fullName);
                    const showTooltip = shouldTruncateDatabaseName(fullName);
                    
                    return (
                      <>
                        <span>{displayName}</span>
                        {showTooltip && (
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                            {fullName}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900 dark:border-b-gray-100"></div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
              
              {/* Separator */}
              <span className="text-gray-400">•</span>
              
              {/* Error Status and Performance */}
              <span className="text-red-600 dark:text-red-400 font-medium">Query Failed</span>
              <span className={`font-medium ${getPerformanceColor(result.executionTimeMs)}`}>
                ⚡{formatExecutionTime(result.executionTimeMs, result.executionTime)}
              </span>
              <span className="text-red-600 dark:text-red-400">
                {result.timestamp.toLocaleTimeString()}
              </span>
            </div>
            
            {/* Right side: Result tabs and Export button */}
            <div className="flex items-center space-x-3">
              
              {/* Export button */}
              {onExport && (
                <button type="button"
                  onClick={onExport}
                  className="flex items-center px-2 py-1 text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Export Results"
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Query Execution Failed
            </h3>
            <p className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded border font-mono">
              {result.message || 'An unknown error occurred'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col" ref={containerRef}>
      {/* Consolidated Query Info Header */}
      <div className="p-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 text-sm">
            {/* Title and Database Context */}
            <h3 className="font-semibold text-gray-900 dark:text-white">Query Results</h3>
            {databaseContext?.database && (
              <div className="text-gray-600 dark:text-gray-400 relative group">
                {(() => {
                  const fullName = databaseContext.connectionType === 'postgresql' && databaseContext.schema
                    ? `${databaseContext.database}.${databaseContext.schema}`
                    : databaseContext.database;
                  const displayName = getTruncatedDatabaseName(fullName);
                  const showTooltip = shouldTruncateDatabaseName(fullName);
                  
                  return (
                    <>
                      <span>{displayName}</span>
                      {showTooltip && (
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                          {fullName}
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900 dark:border-b-gray-100"></div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            
            {/* Separator */}
            <span className="text-gray-400">•</span>
            
            {/* Performance */}
            <span className={`font-medium ${getPerformanceColor(result.executionTimeMs)}`}>
              ⚡{formatExecutionTime(result.executionTimeMs, result.executionTime)}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              ({(result.executionTimeMs ?? 0) < 100 ? 'Fast' : (result.executionTimeMs ?? 0) < 1000 ? 'Good' : (result.executionTimeMs ?? 0) < 5000 ? 'Slow' : 'Very Slow'})
            </span>
            
            {/* Results Summary */}
            {safeResult.rows.length > 0 ? (
              <span className="text-gray-600 dark:text-gray-400">Rows: {safeResult.rows.length.toLocaleString()}</span>
            ) : result.affectedRows !== undefined ? (
              <span className="text-gray-600 dark:text-gray-400">Rows: {result.affectedRows.toLocaleString()}</span>
            ) : (
              <span className="text-gray-600 dark:text-gray-400">Rows: {safeResult.rows.length.toLocaleString()}</span>
            )}
            
            <span className="text-gray-600 dark:text-gray-400">
              {result.timestamp.toLocaleTimeString()}
            </span>
            
            {result.columns.length > 0 && (
              <span className="text-gray-600 dark:text-gray-400">
                {result.columns.length} columns
              </span>
            )}
          </div>
          
          {/* Right side: Result tabs and Export button */}
          <div className="flex items-center space-x-3">
            
            {/* Export button */}
            {onExport && (
              <button type="button"
                onClick={onExport}
                className="flex items-center px-2 py-1 text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                title="Export Results"
              >
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Results */}
      {safeResult.columns.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Query Executed Successfully
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {result.affectedRows !== undefined 
                ? `${result.affectedRows} row(s) affected`
                : safeResult.rows.length > 0 
                  ? `${safeResult.rows.length} row(s) affected`
                  : 'Query completed with no results'
              }
            </p>
          </div>
        </div>
      ) : safeResult.rows.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Data
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              The query returned no results.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto min-w-0" ref={parentRef}>
          <div className="relative" style={{ minWidth: `${safeResult.columns.length ? totalTableWidth : emptyTableWidth}px` }}>
            {/* Table Header */}
            <div className="sticky top-0 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 z-10" style={{ width: `${safeResult.columns.length ? totalTableWidth : emptyTableWidth}px` }}>
              <div className="flex">
                <div className="w-10 px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                  #
                </div>
                {safeResult.columns.map((column, index) => {
                  const columnName = typeof column === 'string' ? column : JSON.stringify(column);
                  const columnWidth = expandedColumnWidths[index] ?? 120;
                  const showTooltip = shouldTruncateColumn(columnName);
                  const displayName = getTruncatedColumnName(columnName);
                  
                  return (
                    <div
                      key={`col-${index}`}
                      style={{ minWidth: `${columnWidth}px`, maxWidth: `${columnWidth}px` }}
                      className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 relative group"
                      title={showTooltip ? columnName : undefined}
                    >
                      <span className="truncate block">
                        {displayName}
                      </span>
                      {showTooltip && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 max-w-xs">
                          {columnName}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Virtual Table Body */}
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: `${safeResult.columns.length ? totalTableWidth : emptyTableWidth}px`,
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const row = safeResult.rows[virtualRow.index]
                const safeRow = Array.isArray(row) ? row : Object.values(row)

                return (
                  <div
                    key={virtualRow.index}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: `${safeResult.columns.length ? totalTableWidth : emptyTableWidth}px`,
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                    className="flex items-center border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <div className="w-10 px-2 py-1.5 text-center text-xs text-gray-500 dark:text-gray-400">
                      {virtualRow.index + 1}
                    </div>
                    {safeRow.map((cell, cellIndex) => {
                      const columnWidth = expandedColumnWidths[cellIndex] ?? 120;
                      
                      return (
                        <div
                          key={cellIndex}
                          style={{ minWidth: `${columnWidth}px`, maxWidth: `${columnWidth}px` }}
                          className="px-2 py-1.5 text-xs text-gray-900 dark:text-white overflow-hidden relative"
                        >
                          {formatCellValue(cell, safeResult.columns[cellIndex], virtualRow.index, columnWidth)}
                        </div>
                      );
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
      
      <DataViewerModal
        isOpen={dataViewerModal.isOpen}
        onClose={() => { setDataViewerModal({ ...dataViewerModal, isOpen: false }) }}
        columnName={dataViewerModal.columnName}
        value={dataViewerModal.value}
        rowIndex={dataViewerModal.rowIndex}
      />
    </div>
  )
}
